import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { CertificationMonth, getCertificationStats } from '../../api/certifications';
import { TableSkeleton } from './CertificationSkeleton';
import './CertificationPage.css';

const formatMonth = (month: string): string => {
  const [year, number] = month.split('-').map(Number);
  // Noon on the 1st, in UTC, so no time zone moves it into another month.
  const date = new Date(Date.UTC(year, number - 1, 1, 12));
  return new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(date);
};

// What this certifier did per month, the numbers their bill is based on.
const CertificationStatsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [months, setMonths] = useState<CertificationMonth[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!currentUser) return;
      try {
        const data = await getCertificationStats(await currentUser.getIdToken());
        if (!cancelled) setMonths(data);
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'Could not load the statistics.');
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  return (
    <div className="certification-page">
      <h1>Statistics</h1>
      <p className="certification-intro">
        The certifications you processed per month. Coepi pays once per case, so a case you certify
        again is listed under Repeats and not billed again.
      </p>
      {error && <div className="certification-error" role="alert">{error}</div>}
      {!error && months === null && <TableSkeleton columns={4} rows={3} />}
      {months && months.length === 0 && <p className="certification-empty">Nothing processed yet.</p>}
      {months && months.length > 0 && (
        <table className="certification-table">
          <thead>
            <tr>
              <th>Month</th>
              <th>Certified</th>
              <th>Repeats (not billed)</th>
              <th>Declined</th>
            </tr>
          </thead>
          <tbody>
            {months.map(row => (
              <tr key={row.month}>
                <td>{formatMonth(row.month)}</td>
                <td>{row.certified}</td>
                <td>{row.repeats ?? 0}</td>
                <td>{row.declined}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default CertificationStatsPage;
